/* The Putting Room: product gallery carousel (one image at a time, looping, swipe / arrows / dots / keys)
   and homepage kit tiles that cycle through the product's photos while hovered (desktop only). */
(() => {
  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

  /* ---------- Product gallery ---------- */
  function initCarousel(root) {
    if (root.dataset.tprReady) return;
    const track = root.querySelector('[data-tpr-track]');
    if (!track) return;
    const slides = [...track.querySelectorAll(':scope > [data-tpr-slide]')];
    const count = slides.length;
    if (count < 2) return;
    root.dataset.tprReady = 'true';

    const dots = [...root.querySelectorAll('[data-tpr-dot]')];

    // Clones at both ends make the loop seamless: [last] 1 2 ... n [first].
    const makeClone = (slide) => {
      const clone = slide.cloneNode(true);
      clone.removeAttribute('data-tpr-slide');
      clone.setAttribute('aria-hidden', 'true');
      clone.setAttribute('inert', '');
      clone.querySelectorAll('[id]').forEach((el) => el.removeAttribute('id'));
      clone.querySelectorAll('img').forEach((img) => {
        img.loading = 'lazy';
        img.removeAttribute('fetchpriority');
      });
      return clone;
    };
    track.prepend(makeClone(slides[count - 1]));
    track.append(makeClone(slides[0]));

    const width = () => Math.max(track.clientWidth, 1);
    const position = () => Math.round(track.scrollLeft / width());
    const realIndex = (pos) => (((pos - 1) % count) + count) % count;
    const jumpTo = (pos) => {
      track.scrollLeft = pos * width();
    };

    // Same synchronous task as the prepend, so the first paint already shows image 1.
    jumpTo(1);

    let current = 0;
    const setActive = (index) => {
      if (index === current) return;
      current = index;
      dots.forEach((dot, n) => {
        const on = n === index;
        dot.classList.toggle('is-active', on);
        if (on) dot.setAttribute('aria-current', 'true');
        else dot.removeAttribute('aria-current');
      });
    };

    const settle = () => {
      const pos = position();
      if (pos <= 0) jumpTo(count);
      else if (pos >= count + 1) jumpTo(1);
      setActive(realIndex(position()));
    };

    let frame = 0;
    let timer = 0;
    track.addEventListener(
      'scroll',
      () => {
        cancelAnimationFrame(frame);
        frame = requestAnimationFrame(() => setActive(realIndex(position())));
        if (!('onscrollend' in window)) {
          clearTimeout(timer);
          timer = setTimeout(settle, 140);
        }
      },
      { passive: true }
    );
    if ('onscrollend' in window) track.addEventListener('scrollend', settle);

    const go = (step) => {
      let pos = position();
      // If a previous move is still sitting on a clone, normalise first.
      if (pos <= 0) {
        jumpTo(count);
        pos = count;
      } else if (pos >= count + 1) {
        jumpTo(1);
        pos = 1;
      }
      track.scrollTo({ left: (pos + step) * width(), behavior: reduceMotion.matches ? 'auto' : 'smooth' });
      if (reduceMotion.matches) settle();
    };

    root.querySelector('[data-tpr-prev]')?.addEventListener('click', () => go(-1));
    root.querySelector('[data-tpr-next]')?.addEventListener('click', () => go(1));
    dots.forEach((dot, n) =>
      dot.addEventListener('click', () => {
        track.scrollTo({ left: (n + 1) * width(), behavior: reduceMotion.matches ? 'auto' : 'smooth' });
        if (reduceMotion.matches) settle();
      })
    );
    track.addEventListener('keydown', (event) => {
      if (event.key === 'ArrowLeft') {
        event.preventDefault();
        go(-1);
      } else if (event.key === 'ArrowRight') {
        event.preventDefault();
        go(1);
      }
    });

    // Keep the current image aligned when the layout width changes.
    if ('ResizeObserver' in window) {
      let lastWidth = track.clientWidth;
      new ResizeObserver(() => {
        if (track.clientWidth === lastWidth) return;
        lastWidth = track.clientWidth;
        jumpTo(current + 1);
      }).observe(track);
    }
  }

  /* ---------- Homepage tiles: cycle photos on hover ---------- */
  const canHover = window.matchMedia('(hover: hover) and (pointer: fine) and (min-width: 750px)');

  function initCycle(card) {
    if (card.dataset.tprReady) return;
    const alts = [...card.querySelectorAll('[data-tpr-cycle-img]')];
    if (!alts.length) return;
    card.dataset.tprReady = 'true';

    const load = () => {
      alts.forEach((img) => {
        if (img.dataset.srcset) {
          img.srcset = img.dataset.srcset;
          img.removeAttribute('data-srcset');
        }
        if (img.dataset.src) {
          img.src = img.dataset.src;
          img.removeAttribute('data-src');
        }
      });
    };

    let step = 0;
    let interval = 0;
    let delay = 0;
    const show = (n) => {
      step = n;
      alts.forEach((img, i) => img.classList.toggle('is-shown', i === n - 1));
    };
    const advance = () => show((step + 1) % (alts.length + 1));
    const stop = () => {
      clearTimeout(delay);
      clearInterval(interval);
      show(0);
    };

    card.addEventListener('pointerenter', (event) => {
      if (event.pointerType !== 'mouse' || !canHover.matches || reduceMotion.matches) return;
      load();
      clearTimeout(delay);
      clearInterval(interval);
      delay = setTimeout(() => {
        advance();
        interval = setInterval(advance, 1200);
      }, 450);
    });
    card.addEventListener('pointerleave', stop);
    card.addEventListener('blur', stop, true);

    // Warm the extra photos once the page is idle, desktop only.
    if (canHover.matches) {
      const warm = () => load();
      if ('requestIdleCallback' in window) window.requestIdleCallback(warm, { timeout: 4000 });
      else setTimeout(warm, 2500);
    }
  }

  const init = () => {
    document.querySelectorAll('[data-tpr-carousel]').forEach(initCarousel);
    document.querySelectorAll('[data-tpr-cycle]').forEach(initCycle);
  };

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
  else init();
  document.addEventListener('shopify:section:load', init);
})();
